import axios from "axios";
import parseLinkHeaderLib from "parse-link-header";
import { getLocalStorageItemCaseInsensitive } from "@/utils/storage.js";

let controller = new AbortController();

// Blobレスポンスのエラー時にJSONに変換するインターセプター
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // エラーレスポンスがBlobの場合、JSONに変換
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const jsonData = JSON.parse(text);
        error.response.data = jsonData;
      } catch (err) {
        console.error("Failed to parse blob error response:", err);
      }
    }
    return Promise.reject(error);
  },
);

function addAbortController(options) {
  if (controller == null) {
    controller = new AbortController();
  }
  options["signal"] = controller.signal;
}

function addCommonHeaders(options) {
  if (!options.headers) {
    return;
  }

  options.headers["X-App-Id"] = __APP_ID__;
  options.headers["X-App-Version"] = __APP_VERSION__;

  const backendVariant =
    getLocalStorageItemCaseInsensitive("X-Backend-Variant");
  if (backendVariant) {
    options.headers["X-Backend-Variant"] = backendVariant;
  }
}

function sendApi(url, options, method, data, funcOK, funcNG) {
  addCommonHeaders(options);

  addAbortController(options);

  if (method == "GET") {
    axios
      .get(url, options)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          console.log("canceled");
          return;
        }
        funcNG(e);
        console.log("e:" + e);
      });
  } else if (method == "POST") {
    axios
      .post(url, data, options)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          console.log("canceled");
          return;
        }
        funcNG(e);
      });
  } else if (method == "PUT") {
    axios
      .put(url, data, options)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          console.log("canceled");
          return;
        }
        funcNG(e);
      });
  } else if (method == "DELETE") {
    axios
      .delete(url, options)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          console.log("canceled");
          return;
        }
        funcNG(e);
      });
  }
}

function parseLinkHeader(linkHeader) {
  if (!linkHeader) {
    return {};
  }

  return parseLinkHeaderLib(linkHeader) ?? {};
}

function getResponseHeader(headers, name) {
  if (!headers) {
    return "";
  }

  if (typeof headers.get === "function") {
    return headers.get(name) ?? "";
  }

  const headerName = Object.keys(headers).find(
    (key) => key.toLowerCase() === name.toLowerCase(),
  );
  return headerName ? headers[headerName] : "";
}

export default {
  sendApi_1_1_Post(url, data, _accessToken = "", funcOK, funcNG) {
    const options = {
      headers: {
        "Content-type": "application/json",
      },
      withCredentials: false,
    };

    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;

    sendApi(url, options, "POST", data, funcOK, funcNG);
  },

  sendApi_1_1_Put(url, data, _accessToken = "", funcOK, funcNG) {
    const options = {
      headers: {
        "Content-type": "application/json",
      },
      withCredentials: false,
    };

    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;

    sendApi(url, options, "PUT", data, funcOK, funcNG);
  },

  sendApi_1_1_Delete(url, _accessToken, funcOK, funcNG, params = {}) {
    const options = {
      headers: {
        "Content-type": "application/json",
      },
      params: params,
      withCredentials: false,
    };

    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;

    sendApi(url, options, "DELETE", null, funcOK, funcNG);
  },

  sendApi_1_1_Bearer_Get(url, _accessToken, funcOK, funcNG, params = {}) {
    const allData = [];

    const fetchPage = (pageUrl = url, pageParams = null) => {
      const options = {
        headers: {
          "Content-type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: "Bearer " + _accessToken,
        },
        params: pageParams ?? params,
        withCredentials: false,
      };

      sendApi(
        pageUrl,
        options,
        "GET",
        null,
        (response) => {
          if (!Array.isArray(response.data)) {
            funcOK(response);
            return;
          }

          allData.push(...response.data);

          const linkHeader = getResponseHeader(response.headers, "link");
          const links = parseLinkHeader(linkHeader);
          if (links.next?.url) {
            fetchPage(links.next.url, {});
            return;
          }

          funcOK({ ...response, data: allData });
        },
        funcNG,
      );
    };

    fetchPage();
  },

  async sendApi_1_1_Bearer_GetNdjsonStream(
    url,
    accessToken,
    funcOK,
    funcNG,
    params = {},
  ) {
    const items = [];

    try {
      const query = new URLSearchParams(params).toString();
      const requestUrl = query ? `${url}?${query}` : url;
      const headers = {
        "Content-type": "application/json",
        authorization: `Bearer ${accessToken}`,
        "X-App-Id": __APP_ID__,
        "X-App-Version": __APP_VERSION__,
      };

      const backendVariant =
        getLocalStorageItemCaseInsensitive("X-Backend-Variant");
      if (backendVariant) {
        headers["X-Backend-Variant"] = backendVariant;
      }

      const options = {
        method: "GET",
        headers,
      };
      addAbortController(options);

      const response = await fetch(requestUrl, options);

      if (!response.ok) {
        throw response;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let done = false;

      while (!done) {
        const result = await reader.read();
        done = result.done;

        if (done) {
          break;
        }

        buffer += decoder.decode(result.value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          items.push(JSON.parse(line));
        }
      }

      buffer += decoder.decode();

      if (buffer.trim()) {
        items.push(JSON.parse(buffer));
      }

      funcOK({ data: items });
    } catch (e) {
      if (e.name === "AbortError") {
        console.log("canceled");
        return;
      }
      funcNG(e);
    }
  },

  sendApi_1_1_Bearer_GetAll(sendDataList, accessToken, funcOK, funcNG) {
    this.bulkSendApi(
      sendDataList.map((sendData) => {
        return {
          method: "get",
          url: sendData.url,
          params: sendData.params,
        };
      }),
      accessToken,
      funcOK,
      funcNG,
    );
  },

  async sendApi_1_1_Bearer_GetAllSerial(
    sendDataList,
    accessToken,
    funcOK,
    funcNG,
  ) {
    if (controller == null) {
      controller = new AbortController();
    }

    const responses = [];

    try {
      for (const sendData of sendDataList) {
        let options = {
          method: "get",
          url: sendData.url,
          headers: {
            "Content-type": "application/json",
            "Access-Control-Allow-Origin": "*",
            authorization: "Bearer " + accessToken,
          },
          params: sendData.params,
          withCredentials: false,
        };
        addCommonHeaders(options);
        addAbortController(options);
        responses.push(await axios(options));
      }

      funcOK(responses);
    } catch (e) {
      funcNG(e);
    }
  },

  sendApi_1_1_Bearer_PutAll(sendDataList, accessToken, funcOK, funcNG) {
    this.bulkSendApi(
      sendDataList.map((sendData) => {
        return {
          method: "put",
          url: sendData.url,
          data: sendData.data,
        };
      }),
      accessToken,
      funcOK,
      funcNG,
    );
  },

  sendApi_1_1_Bearer_DeleteAll(sendDataList, accessToken, funcOK, funcNG) {
    this.bulkSendApi(
      sendDataList.map((sendData) => {
        return {
          method: "delete",
          url: sendData.url,
          params: sendData.params,
        };
      }),
      accessToken,
      funcOK,
      funcNG,
    );
  },

  // サーバー側で同時接続数の制限が設けられた場合、処理を見直す可能性あり
  bulkSendApi(sendDataList, accessToken, funcOK, funcNG) {
    if (controller == null) {
      controller = new AbortController();
    }

    const axiosPromises = sendDataList.map((sendData) => {
      let options = {
        method: sendData.method,
        url: sendData.url,
        headers: {
          "Content-type": "application/json",
          "Access-Control-Allow-Origin": "*",
          authorization: "Bearer " + accessToken,
        },
        params: sendData.params,
        data: sendData.data,
        withCredentials: false,
      };
      addCommonHeaders(options);
      addAbortController(options);
      return axios(options);
    });

    Promise.all(axiosPromises)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        funcNG(e);
      });
  },

  sendApi_1_1_MultipartPost(url, data, _accessToken = "", funcOK, funcNG) {
    const options = {
      headers: {
        "content-type": "multipart/form-data",
      },
      withCredentials: false,
    };
    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;

    sendApi(url, options, "POST", data, funcOK, funcNG);
  },
  sendApi_1_1_MultipartDelete(url, Data, _accessToken = "", funcOK, funcNG) {
    const options = {
      headers: {
        "Content-type": "application/json",
      },
      withCredentials: false,
    };
    options.data = Data;
    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;
    sendApi(url, options, "DELETE", null, funcOK, funcNG);
  },
  sendApi_1_1_FileGet(url, _accessToken, funcOK, funcNG, params = {}) {
    const options = {
      headers: {},
      params: params,
      withCredentials: false,
      responseType: "blob",
    };
    if (_accessToken) options.headers.authorization = "Bearer " + _accessToken;
    sendApi(url, options, "GET", null, funcOK, funcNG);
  },
  sendApi_1_1_FileGetAll(sendDataList, _accessToken, funcOK, funcNG) {
    if (controller == null) {
      controller = new AbortController();
    }

    const axiosPromises = sendDataList.map((sendData) => {
      const options = {
        method: "get",
        url: sendData.url,
        headers: {},
        params: sendData.params,
        withCredentials: false,
        responseType: "blob",
      };

      if (_accessToken) {
        options.headers.authorization = "Bearer " + _accessToken;
      }

      addCommonHeaders(options);
      addAbortController(options);
      return axios(options);
    });

    Promise.all(axiosPromises)
      .then((response) => {
        funcOK(response);
      })
      .catch((e) => {
        funcNG(e);
      });
  },
  cancelCall() {
    if (controller != null) {
      controller.abort();
      controller = null;
    }
  },
};
